'use client'

import { ArrowUpRight, Play } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import { LocaleLink } from '@/components/LocaleLink'
import { useMemo, useState } from 'react'

import { cn } from '@/utilities/ui'
import { loaderDeCandidatos } from '@/components/Media/ImageMedia/payloadSizesLoader'
// IMPORTANTE: importar dos caminhos diretos (NÃO do barrel '@/components/home-editorial'),
// senão o client puxa HomeWidgetSidebar (server → payload → fs) e quebra o bundle.
import { HomeHeaderFilterBar } from '@/components/home-editorial/HomeHeaderFilterBar'
import { externalArticleLink } from '@/components/home-editorial/common'
import {
  HOME_BI_NEWS_IMAGE_HOVER,
  HOME_BI_NEWS_ITEM_GROUP,
  HOME_BI_NEWS_TITLE_LINK,
  HOME_LATEST_SIDEBAR_THUMB,
  TOUT_LAYOUT_HEADING_BAR,
} from '@/components/home-editorial/home-bi-layout'
import type {
  HomeFeedQuery,
  HomeHeaderFilterButton,
  LatestSidebarItem,
} from '@/components/home-editorial/types'

type Props = {
  heading?: string
  showHeading?: boolean
  moreLabel?: string
  moreHref?: string
  /** ON → thumb 100×75; OFF → só-texto (▶ se vídeo). */
  showThumbnail?: boolean
  /** Pills de filtro (Header Filter). Undefined/empty → sem barra de filtros. */
  buttons?: HomeHeaderFilterButton[]
  baseQuery: HomeFeedQuery
  limit: number
  initialItems: LatestSidebarItem[]
}

const ENDPOINT = '/home-feed/band'
const DEFAULT_ID = '__default__'

function PublishDate({ label }: { label: string }) {
  if (!label) return null
  return (
    <time className="publish-date text-muted-foreground text-[11px] font-semibold tracking-wide">
      {label}
    </time>
  )
}

function LatestItemText({ item }: { item: LatestSidebarItem }) {
  return (
    <article
      className={cn(
        'tout with-tout-tag with-ungrouped-text flex flex-col gap-1',
        HOME_BI_NEWS_ITEM_GROUP,
      )}
    >
      <PublishDate label={item.publishedLabel} />
      <h3 className="tout-title text-sm leading-snug font-semibold">
        <LocaleLink
          {...externalArticleLink}
          href={item.href}
          className={cn(HOME_BI_NEWS_TITLE_LINK, 'tout-title-link inline-flex items-start gap-1.5')}
        >
          {item.isVideo ? (
            <Play className="text-foreground mt-0.5 size-3.5 shrink-0" aria-hidden />
          ) : null}
          <span>{item.title}</span>
        </LocaleLink>
      </h3>
    </article>
  )
}

function LatestItemThumb({ item }: { item: LatestSidebarItem }) {
  const t = useTranslations('Sidebar')
  const loaderPayload = useMemo(
    () =>
      item.imgSizes && item.imgSizes.length >= 2 ? loaderDeCandidatos(item.imgSizes) : undefined,
    [item.imgSizes],
  )
  return (
    <article className={cn('tout with-thumb flex items-start gap-3', HOME_BI_NEWS_ITEM_GROUP)}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <PublishDate label={item.publishedLabel} />
        <h3 className="tout-title text-sm leading-snug font-semibold">
          <LocaleLink
            {...externalArticleLink}
            href={item.href}
            className={cn(HOME_BI_NEWS_TITLE_LINK, 'tout-title-link')}
          >
            <span className="line-clamp-3">{item.title}</span>
          </LocaleLink>
        </h3>
      </div>
      {item.img ? (
        <LocaleLink
          {...externalArticleLink}
          href={item.href}
          className={HOME_LATEST_SIDEBAR_THUMB}
          tabIndex={-1}
          aria-label={t('itemImageLabel', { title: item.title })}
        >
          <Image
            src={item.img}
            alt={item.alt ?? item.title}
            fill
            className={HOME_BI_NEWS_IMAGE_HOVER}
            sizes="100px"
            // srcset dos tamanhos do Payload (performance, 19/08/2026): uma miniatura de 100 px
            // baixava o original de 1200 px. Sem tamanhos ⇒ caminho de sempre.
            loader={loaderPayload}
            // Mídia R2 (URL absoluta) atrás da Cloudflare → 403 no otimizador; servir direto.
            unoptimized={loaderPayload ? false : /^https?:\/\//i.test(item.img)}
          />
        </LocaleLink>
      ) : null}
    </article>
  )
}

/**
 * Widget "Últimas notícias" da sidebar. Um único código: `showThumbnail` decide thumb vs só-texto;
 * os pills (Header Filter) re-consultam via `/home-feed/band` (shape `sidebarLatest`).
 */
export function LatestSidebarWidget({
  heading,
  showHeading = true,
  moreLabel,
  moreHref = '/posts',
  showThumbnail = true,
  buttons,
  baseQuery,
  limit,
  initialItems,
}: Props) {
  const t = useTranslations('Sidebar')
  // G10 (revisão 2026-06-14): refetch (pill) precisa carregar o locale ativo; sem isto o POST a
  // /home-feed/band caía no default e misturava PT em /en e /es.
  const locale = useLocale()
  const resolvedHeading = heading ?? t('latestHeading')
  const resolvedMoreLabel = moreLabel ?? t('latestMoreLabel')
  const [activeId, setActiveId] = useState(DEFAULT_ID)
  const [items, setItems] = useState<LatestSidebarItem[]>(initialItems)
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)

  const hasFilters = Boolean(buttons && buttons.length > 0)

  const onSelect = async (button: HomeHeaderFilterButton) => {
    if (loading || button.id === activeId) return
    setActiveId(button.id)
    setErrored(false)
    if (!button.override) {
      setItems(initialItems)
      return
    }
    setLoading(true)
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          shape: 'sidebarLatest',
          limit,
          query: baseQuery,
          override: button.override,
          locale,
        }),
      })
      if (!res.ok) throw new Error('bad response')
      const data = (await res.json()) as { items?: LatestSidebarItem[] }
      setItems(data.items ?? [])
    } catch {
      setErrored(true)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <section
      className="latest tout-layout min-w-0"
      data-sidebar-widget="latest"
      data-component-type="tout-layout"
      data-latest-variant={showThumbnail ? 'thumb' : 'text'}
    >
      {showHeading ? (
        <header className="tout-layout-header mb-6 flex items-start justify-between gap-3">
          <h2
            className={cn(
              'tout-layout-heading text-lg leading-snug font-semibold tracking-tight',
              TOUT_LAYOUT_HEADING_BAR,
            )}
          >
            {resolvedHeading}
          </h2>
          {moreHref ? (
            <LocaleLink
              href={moreHref}
              data-component-type="cta-more-link-arrow"
              className="cta-more-link-arrow group inline-flex h-8 min-h-8 shrink-0 items-center gap-2 rounded-sm py-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              <span className="link-text text-xs leading-none font-semibold tracking-wide whitespace-nowrap">
                {resolvedMoreLabel}
              </span>
              <span
                className="icon-wrapper bg-foreground text-background flex size-6 shrink-0 items-center justify-center rounded-full transition-opacity group-hover:opacity-90"
                aria-hidden
              >
                <ArrowUpRight className="size-3" strokeWidth={2.25} />
              </span>
            </LocaleLink>
          ) : null}
        </header>
      ) : null}

      <section
        className="latest-feed"
        data-component-type="latest-feed"
        data-current-filter={activeId}
      >
        {hasFilters ? (
          <header className="filters mb-4">
            <HomeHeaderFilterBar
              buttons={buttons!}
              activeId={activeId}
              onSelect={onSelect}
              loading={loading}
              ariaLabel={t('filterLatestAria')}
            />
          </header>
        ) : null}

        <section
          className={cn(
            'feed-list flex flex-col gap-4 transition-opacity',
            loading && 'opacity-60',
          )}
          aria-live="polite"
          aria-busy={loading || undefined}
        >
          {items.length === 0 ? (
            <p className="text-muted-foreground text-sm" role="status">
              {errored ? t('latestLoadError') : t('latestEmptyCategory')}
            </p>
          ) : showThumbnail ? (
            items.map((item) => <LatestItemThumb key={item.id} item={item} />)
          ) : (
            items.map((item) => <LatestItemText key={item.id} item={item} />)
          )}
        </section>
      </section>
    </section>
  )
}
